import React, { useState, useEffect, useRef } from "react";
// import SearchIcon from "@mui/icons-material/Search";
import { useRouter } from "next/router";
import { HiX } from "react-icons/hi";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useAuthenticator, Collection } from "@aws-amplify/ui-react";
import { generateClient } from "aws-amplify/api";
import { Loader, Loader2, ButtonLoader } from "./Loader";
import { categories } from "../data/storyflow";
import {
  discoverImageIds,
  discoverImageIdentityIds,
  discoverImageUrls,
} from "../data/discover";
import { getArtCustom3, listArtsCustom2 } from "../src/graphql/custom_queries";
import {
  ModelArtFilterInput,
  ModelArtConditionInput,
  ModelBooleanInput,
  listArtsCustom2Query,
  getArtCustom3Query,
} from "../src/API";
import LoginComponent from "./LoginModal";

const ImageCard = dynamic(() => import("./ImageCard"));

interface DiscoverProps {
  id: string;
}

const DiscoverComponent: React.FC<DiscoverProps> = ({ id }) => {
  let idIndex: number = null;
  let fromShare: boolean = false;
  let shareId: string;
  if (id !== null) {
    idIndex = -1;
    fromShare = true;
    shareId = id.split("/")[1];
  }
  const router = useRouter();
  const client = generateClient();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const handleContextMenu = (event: React.MouseEvent<HTMLImageElement>) => {
    event.preventDefault();
  };
  const handleDragStart = (event: React.DragEvent<HTMLImageElement>) => {
    event.preventDefault();
  };
  const [applyView, setApplyView] = useState<boolean>(false);
  const [imageSelected, setImageSelected] = useState<boolean>(fromShare);
  const [selectedIndex, setSelectedIndex] = useState<number>(idIndex);
  const [highQualityUrl, setHighQualityUrl] = useState<string>("");
  const [imageDetails, setImageDetails] = useState<getArtCustom3Query>({});
  const [imageKeys, setImageKeys] = useState([]);
  const [imageIds, setImageIds] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showCategories, setShowCategories] = useState({}); // Initialize state for toggling each category
  const [loading, setLoading] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [showLogin, setShowLogin] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [browserWindowHeight, setBrowserWindowHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 0,
  );
  const [browserWindowWidth, setBrowserWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  // Function to update browserWindowHeight when the window is resized
  const updateBrowserWindowHeight = () => {
    setBrowserWindowHeight(window.innerHeight);
  };
  const updateBrowserWindowWidth = () => {
    setBrowserWindowWidth(window.innerWidth);
  };

  // Add an event listener to the window for the "resize" event
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateBrowserWindowHeight);
      window.addEventListener("resize", updateBrowserWindowWidth);

      // Cleanup the event listener when the component unmounts
      return () => {
        window.removeEventListener("resize", updateBrowserWindowHeight);
        window.removeEventListener("resize", updateBrowserWindowWidth);
      };
    }
  }, []);

  useEffect(() => {
    // If ID is in the search parameters of the URL lets update the metadata
    if (id !== null) {
      console.log("Updating meta");
      // Define meta tags based on the 'id' parameter
      const metaTags = {
        title: "Page Title for ID 123456",
        description: "Description for ID 123456",
        image: `${highQualityUrl}`,
        imagetype: "image/png",
        imagewidth: "200",
        imageheight: "200",
      };

      const updateMetaTag = (property: string, content: string) => {
        let metaTag = document.querySelector(
          `meta[property="${property}"]`,
        ) as HTMLMetaElement | null;

        if (!metaTag) {
          // Create a new meta tag if it doesn't exist
          metaTag = document.createElement("meta");
          metaTag.setAttribute("property", property);
          document.head.appendChild(metaTag);
        }

        // Set the content of the meta tag
        metaTag.content = content;
      };

      // Update meta tags based on the 'id' parameter
      updateMetaTag("og:title", metaTags.title);
      updateMetaTag("og:description", metaTags.description);
      updateMetaTag("og:image", metaTags.image);
      updateMetaTag("og:image:type", metaTags.imagetype);
      updateMetaTag("og:image:width", metaTags.imagewidth);
      updateMetaTag("og:image:height", metaTags.imageheight);
    }
  }, [id, highQualityUrl]);

  useEffect(() => {
    async function fetchFirstImages() {
      const filterdeleted: ModelBooleanInput = { ne: true };
      const filterapprove: ModelBooleanInput = { ne: false };
      // const filterlike: ModelBooleanInput = { eq: true };
      const filter: ModelArtFilterInput = {
        deleted: filterdeleted,
        approve: filterapprove,
        // like: filterlike,
      };
      const limit: number = 100;

      const request = await client.graphql({
        authMode: "apiKey",
        query: listArtsCustom2,
        variables: {
          filter,
          limit,
        },
      });
      return request.data as listArtsCustom2Query;
    }
    async function fetchNextImages(nextToken: string) {
      const filterdeleted: ModelBooleanInput = { ne: true };
      const filterapprove: ModelBooleanInput = { ne: false };
      // const filterlike: ModelBooleanInput = { eq: true };
      const filter: ModelArtFilterInput = {
        deleted: filterdeleted,
        approve: filterapprove,
        // like: filterlike,
      };
      const limit: number = 100;

      const request = await client.graphql({
        authMode: "apiKey",
        query: listArtsCustom2,
        variables: {
          filter,
          limit,
          nextToken,
        },
      });
      return request.data as listArtsCustom2Query;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async function getImages() {
      const firstImages: listArtsCustom2Query = await fetchFirstImages();
      let imageItems = firstImages.listArts?.items;
      let ids: string[];
      let identityId: string[];
      let nextImages: listArtsCustom2Query;
      let nexttoken = firstImages.listArts.nextToken;
      let nextids: string[];
      let nextIdentityId: string[];
      if (imageItems) {
        [ids, identityId] = imageItems.reduce(
          (acc, art) => {
            acc[0].push(art.id);
            acc[1].push(art.s3Image.key);
            return acc;
          },
          [[], []],
        );
      }
      while (ids.length < 100) {
        nextImages = await fetchNextImages(nexttoken);
        imageItems = nextImages.listArts.items;
        [nextids, nextIdentityId] = imageItems.reduce(
          (acc, art) => {
            acc[0].push(art.id);
            acc[1].push(art.s3Image.key);
            return acc;
          },
          [[], []],
        );
        ids = ids.concat(nextids);
        identityId = identityId.concat(nextIdentityId);
        nexttoken = nextImages.listArts.nextToken;
        // Break the loop if there are no more next images
        if (!nexttoken) {
          break;
        }
      }
      setImageKeys(identityId);
      setImageIds(ids);
    }
    if (Object.keys(selectedOptions).length === 0) {
      // setApplyView(false);
      // setLoading(true);
      // getImages();
      setImageKeys(discoverImageIdentityIds);
      setImageIds(discoverImageIds);
      setImageUrls(discoverImageUrls);
      setLoading(false);
    }
  }, [client, selectedOptions]);

  async function fetchFilterFirst(filter) {
    const limit: number = 100;

    const request = await client.graphql({
      authMode: "apiKey",
      query: listArtsCustom2,
      variables: {
        filter,
        limit,
      },
    });
    return request.data as listArtsCustom2Query;
  }
  async function fetchFilterNextImages(filter, nextToken: string) {
    const limit: number = 100;

    const request = await client.graphql({
      authMode: "apiKey",
      query: listArtsCustom2,
      variables: {
        filter,
        limit,
        nextToken,
      },
    });
    return request.data as listArtsCustom2Query;
  }

  async function getImagesFilter() {
    setLoading(true);
    const orArraysByKeys: Record<string, Array<ModelArtConditionInput>> = {};

    Object.keys(selectedOptions).forEach((key) => {
      const optionArray = selectedOptions[key];
      if (optionArray && optionArray.length > 0) {
        const conditions = optionArray.map((value) => ({
          [key.toLowerCase()]: { eq: value },
        }));
        orArraysByKeys[key] = conditions;
      }
    });

    const filterString = Object.keys(orArraysByKeys).map((key) => {
      const orArray = orArraysByKeys[key];
      const orItems: ModelArtFilterInput = {
        or: orArray,
      };
      return orItems;
    });

    const filter: ModelArtFilterInput = {
      deleted: { ne: true },
      approve: { ne: false },
      and: filterString,
    };

    const firstImages: listArtsCustom2Query = await fetchFilterFirst(filter);
    let imageItems = firstImages.listArts?.items;
    let ids: string[];
    let identityId: string[];
    let nextImages: listArtsCustom2Query;
    let nexttoken = firstImages.listArts.nextToken;
    let nextids: string[];
    let nextIdentityId: string[];
    if (imageItems) {
      [ids, identityId] = imageItems.reduce(
        (acc, art) => {
          acc[0].push(art.id);
          acc[1].push(art.s3Image.key);
          return acc;
        },
        [[], []],
      );
    }
    while (ids.length < 100) {
      nextImages = await fetchFilterNextImages(filter, nexttoken);
      imageItems = nextImages.listArts.items;
      [nextids, nextIdentityId] = imageItems.reduce(
        (acc, art) => {
          acc[0].push(art.id);
          acc[1].push(art.s3Image.key);
          return acc;
        },
        [[], []],
      );
      ids = ids.concat(nextids);
      identityId = identityId.concat(nextIdentityId);
      nexttoken = nextImages.listArts.nextToken;
      // Break the loop if there are no more next images
      if (!nexttoken) {
        break;
      }
    }
    setImageKeys(identityId);
    setImageIds(ids);
  }

  useEffect(() => {
    async function getImageUrl(imageid: string) {
      try {
        // Submit the body to the API
        const res = await fetch("/api/getImageUrl", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: "discover@raraity.com",
            id: imageid,
          }),
        });

        // Wait for the response
        const response = await res.json();
        return response;
      } catch (error) {
        if (error instanceof Error) {
          console.error(error.message);
        } else {
          console.error(error);
        }
        return error;
      }
    }

    async function fetchImages() {
      try {
        setLoading(true);
        const imagePromises = imageIds.map((imageid) => getImageUrl(imageid));
        const imgUrls = await Promise.all(imagePromises);
        setImageUrls(imgUrls);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    }
    if (Object.keys(selectedOptions).length !== 0) fetchImages();
  }, [imageIds, selectedOptions]);

  const handleOptionChange = (category, option) => {
    setSelectedOptions((prevSelectedOptions) => {
      const updatedOptions = { ...prevSelectedOptions };

      if (Array.isArray(updatedOptions[category])) {
        const index = updatedOptions[category].indexOf(option);

        if (index !== -1) {
          // If the option is already selected, remove it
          updatedOptions[category] = [
            ...updatedOptions[category].slice(0, index),
            ...updatedOptions[category].slice(index + 1),
          ];

          // If the last option is removed, delete the entire array
          if (updatedOptions[category].length === 0) {
            delete updatedOptions[category];
          }
        } else {
          // If the option is not selected, add it
          updatedOptions[category] = [...updatedOptions[category], option];
        }
      } else {
        // If the category is not an array, it means no option is selected yet
        // Create a new array with the selected option
        updatedOptions[category] = [option];
      }

      return updatedOptions;
    });
  };

  // Set initial state for each category
  useEffect(() => {
    const initialShowState = {};
    categories.forEach((category) => {
      initialShowState[category.category] = false;
    });
    setShowCategories(initialShowState);
  }, []);

  // Function to toggle a specific category
  const toggleCategory = (categoryName) => {
    setShowCategories((prevState) => ({
      ...prevState,
      [categoryName]: !prevState[categoryName],
    }));
  };

  useEffect(() => {
    if (Object.keys(selectedOptions).length !== 0) {
      setApplyView(true);
    }
  }, [selectedOptions]);

  useEffect(() => {
    async function fetchHighQuality(imageid: string) {
      try {
        // Submit the body to the API
        const res = await fetch("/api/getImageUrl", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: "discover@raraity.com",
            id: imageid,
            bucket: process.env.NEXT_PUBLIC_BUCKET_NAME,
          }),
        });

        // Wait for the response
        const response = await res.json();
        return response;
      } catch (error) {
        if (error instanceof Error) {
          console.error(error.message);
        } else {
          console.error(error);
        }
        return error;
      }
    }
    async function fetchImageDetails(imageid) {
      try {
        const variables = { id: imageid };
        const response = await client.graphql({
          authMode: "apiKey",
          query: getArtCustom3,
          variables,
        });
        return response.data as getArtCustom3Query;
      } catch (error) {
        console.error("Error fetching art:", error);
        throw error;
      }
    }
    async function storeUrl() {
      if (selectedIndex === -1) {
        const key = `protected/${id}`;
        setHighQualityUrl(await fetchHighQuality(`${key}`));
      } else {
        const key = imageKeys[selectedIndex];
        setHighQualityUrl(await fetchHighQuality(`${key}`));
      }
    }
    async function storeImageDetails() {
      if (selectedIndex === -1) {
        setImageDetails(await fetchImageDetails(shareId));
      } else {
        const imageid = imageIds[selectedIndex];
        setImageDetails(await fetchImageDetails(imageid));
      }
    }

    if (selectedIndex !== null) {
      storeUrl();
      storeImageDetails();
    }
  }, [client, id, imageIds, imageKeys, selectedIndex, shareId]);

  const createScratch = () => {
    router.push("/create-story");
  };

  const createElement = (feeling, subject, medium, styles, color) => {
    const queryParams = new URLSearchParams();
    if (feeling) {
      queryParams.append("feeling", feeling);
    }
    if (subject) {
      queryParams.append("subject", subject);
    }
    // if (subOption) {
    //   queryParams.append("subOption", subOption.replace(/\s/g, ""));
    // }
    if (medium) {
      queryParams.append("medium", medium);
    }
    if (styles) {
      queryParams.append("styles", styles);
    }
    if (color) {
      queryParams.append("color", color);
    }

    const newUrl = `/create-story?${queryParams.toString()}`;
    router.push(newUrl);
  };

  // Event handler for toggling the mounting options visibility
  const toggleDetailsVisibility = () => {
    if (imageSelected) {
      setImageSelected(false);
      setSelectedIndex(null);
    } else {
      setImageSelected(true);
    }
  };

  // Create a ref for the popup containers to check if a click occurred inside them
  const imageSelectedRef = useRef(null);
  const buttonRefs = useRef([]);

  // Add event listeners to handle clicks outside of popups
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click occurred inside the popup
      if (
        imageSelectedRef.current &&
        !imageSelectedRef.current.contains(event.target)
      ) {
        // Check if the click occurred on any of the buttons
        const clickedOnButton = buttonRefs.current.some((ref) =>
          ref.current.contains(event.target),
        );

        // If the click occurred outside the popup and not on any button, close the popup
        if (!clickedOnButton) {
          setImageSelected(false);
          setShowLogin(false);
        }
      }
    };

    // Add the event listener to the document
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [imageSelected]);

  const showLoginComponent = () => {
    setShowLogin(true);
  };

  return (
    <div title="Discover">
      <div className="flex flex-col justify-center items-center py-4">
        {/* Until search is working correctly, we just show Title */}
        <h1>Discover Art</h1>
        {/* <div className="flex justify-center items-center py-4">
              <input
                className="border border-black rounded-full px-5 py-2 w-full md:w-[500px]"
                type="text"
                name="query"
                id="query"
                placeholder="Search"
                value={query}
                onChange={handleInputChange}
              />
              <button
                type="button"
                className="border border-black bg-black text-white rounded-full ml-2 px-5 py-2"
                aria-label="Search"
              >
                <SearchIcon />
              </button>
            </div> */}
      </div>
      <div className="flex">
        <div className="flex flex-col pl-1 lg:pl-2 2xl:pl-4 pt-1 min-w-[150px] sm:min-w-[200px] 2xl:min-w-[300px]">
          <h3 className="flex items-center justify-left text-2xl pl-2 pb-2 font-bold">
            Filter
          </h3>
          {applyView && (
            <>
              <button
                type="submit"
                className="bg-black text-white py-2 px-4 mb-1 hover:bg-gray-800 focus:outline-none active:bg-gray-800 rounded-sm"
                onClick={() => {
                  getImagesFilter();
                }}
              >
                Apply
              </button>
              <button
                type="submit"
                className="bg-white text-black border border-black py-2 px-4 mb-1 hover:bg-gray-200 focus:outline-none active:bg-gray-200 rounded-sm"
                onClick={() => {
                  setApplyView(false);
                  setSelectedOptions({});
                }}
              >
                Clear
              </button>
            </>
          )}
          {categories.map((category) => (
            <div key={category.category} className="flex flex-col mb-0.5">
              <div className="flex flex-col justify-between border border-black pl-2 pr-2 py-4 rounded-sm">
                <div className="flex justify-between">
                  <h2
                    className={
                      browserWindowWidth > 1536
                        ? "text-lg"
                        : browserWindowWidth > 640
                          ? "text-base"
                          : "text-sm"
                    }
                  >
                    {category.category}
                  </h2>
                  <button onClick={() => toggleCategory(category.category)}>
                    {showCategories[category.category] ? "-" : "+"}
                  </button>
                </div>
                {showCategories[category.category] && (
                  <div className="pl-2 pt-2">
                    {category.options.map((option) => (
                      <label
                        key={option.name}
                        className="block"
                        htmlFor={`${category.category}-${option.name}`}
                      >
                        <input
                          className="m-1 2xl:m-2"
                          type="checkbox"
                          name={category.category}
                          value={option.name}
                          checked={
                            Array.isArray(selectedOptions[category.category]) &&
                            selectedOptions[category.category].includes(
                              option.name,
                            )
                          }
                          onChange={() =>
                            handleOptionChange(category.category, option.name)
                          }
                          disabled={loading}
                          id={`${category.category}-${option.name}`}
                        />
                        <span
                          style={{
                            fontSize:
                              browserWindowWidth > 1536
                                ? "1rem"
                                : browserWindowWidth > 640
                                  ? "0.875rem"
                                  : "0.75rem",
                          }}
                        >
                          {option.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {loading ? (
          <div
            className="w-full flex items-center justify-center"
            style={{
              marginLeft:
                browserWindowWidth > 1536
                  ? "300px"
                  : browserWindowWidth > 640
                    ? "200px"
                    : "150px",
            }}
          >
            {browserWindowWidth > 768 ? <Loader /> : <Loader2 />}
          </div>
        ) : imageUrls.length > 0 ? (
          <div
            onContextMenu={handleContextMenu}
            onDragStart={handleDragStart}
            className="w-full justify-center flex flex-wrap mx-2 p-1"
          >
            <Collection
              items={imageUrls}
              type="grid"
              margin="0 auto"
              justifyContent="center"
              templateColumns={{
                base: "repeat(2, minmax(0, 1fr))",
                medium: "repeat(5, minmax(125px, 1fr))",
                large: "repeat(6, minmax(125px, 1fr))",
                xl: "repeat(9, minmax(125px, 1fr))",
              }}
              gap="xs"
            >
              {(item: string, index: number) => (
                <div
                  key={item}
                  className="flex flex-col justify-center items-center"
                  ref={buttonRefs.current[index]}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setHighQualityUrl("");
                      setSelectedIndex(index);
                      toggleDetailsVisibility();
                    }}
                    disabled={imageSelected}
                    aria-label="ImageSelect"
                  >
                    <ImageCard type="discover" item={item} />
                  </button>
                </div>
              )}
            </Collection>
          </div>
        ) : !loading ? (
          <div className="w-full flex justify-center">
            <h1 className="mt-8">No Results</h1>
          </div>
        ) : null}
      </div>

      {imageSelected && highQualityUrl && (
        <>
          <div
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-11/12 sm:w-9/12 md:w-8/12 lg:w-1/2 bg-[white] rounded-md py-3 px-8 shadow-2xl"
            style={{ zIndex: 1 }}
            ref={imageSelectedRef}
          >
            <div className="flex mt-5 pr-5 justify-end">
              <button
                type="button"
                className="flex text-gray-500 hover:text-black cursor-pointer"
                onClick={() => {
                  toggleDetailsVisibility();
                }}
                aria-label="close"
              >
                <HiX fontSize="24px" />
              </button>
            </div>
            <div
              onContextMenu={handleContextMenu}
              onDragStart={handleDragStart}
              className="w-full flex flex-col p-5 items-center justify-center"
            >
              <Image
                width={400}
                height={400}
                src={highQualityUrl}
                alt={imageIds[selectedIndex] || id}
                className="flex items-center justify-center"
                sizes="25vw"
                style={{ objectFit: "contain" }}
                placeholder="blur"
                blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mPsndRbDwAFjQItMLC1WQAAAABJRU5ErkJggg=="
              />
              {Object.keys(imageDetails).length !== 0 && (
                <>
                  <div className="flex w-full sm:w-[400px] justify-between">
                    <div className="flex flex-col justify-start">
                      <p className="font-bold">
                        @{imageDetails.getArt.user?.username}
                      </p>
                      <p className="text-sm">
                        Created:{" "}
                        {new Date(
                          imageDetails.getArt.createdAt,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-col justify-end text-right">
                      <p className="font-bold">Elements</p>
                      <div style={{ maxWidth: "140px" }}>
                        <p className="text-xs">
                          {imageDetails.getArt.styles}
                          {imageDetails.getArt.styles &&
                            (imageDetails.getArt.subject ||
                              imageDetails.getArt.feeling ||
                              imageDetails.getArt.color ||
                              imageDetails.getArt.medium) &&
                            ", "}

                          {imageDetails.getArt.subject}
                          {imageDetails.getArt.subject &&
                            (imageDetails.getArt.feeling ||
                              imageDetails.getArt.color ||
                              imageDetails.getArt.medium) &&
                            ", "}

                          {imageDetails.getArt.feeling}
                          {imageDetails.getArt.feeling &&
                            (imageDetails.getArt.color ||
                              imageDetails.getArt.medium) &&
                            ", "}

                          {imageDetails.getArt.color}
                          {imageDetails.getArt.color &&
                            imageDetails.getArt.medium &&
                            ", "}

                          {imageDetails.getArt.medium}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center items-center my-4">
                    <h3>Ready to Create Your Own?</h3>
                    <div className="flex justify-between">
                      <button
                        type="button"
                        className="w-[165px] text-sm md:text-md bg-white mx-1 my-1 rounded-md py-3 px-4 flex items-center justify-center text-black border border-black cursor-pointer"
                        onClick={() =>
                          authStatus === "authenticated"
                            ? createScratch()
                            : showLoginComponent()
                        }
                      >
                        {loading2 ? <ButtonLoader /> : "Create from scratch"}
                      </button>
                      <button
                        type="button"
                        className="w-[165px] text-sm md:text-md bg-white mx-1 my-1 rounded-md py-3 px-4 flex items-center justify-center text-black border border-black cursor-pointer"
                        onClick={() => {
                          setLoading2(true);
                          if (authStatus === "authenticated") {
                            createElement(
                              imageDetails.getArt.feeling,
                              imageDetails.getArt.subject,
                              imageDetails.getArt.medium,
                              imageDetails.getArt.styles,
                              imageDetails.getArt.color,
                            );
                          } else {
                            showLoginComponent();
                          }
                        }}
                      >
                        {loading2 ? <ButtonLoader /> : "Use These Elements"}
                      </button>
                      {showLogin && <LoginComponent />}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DiscoverComponent;

export async function getStaticProps() {
  return {
    props: { page: "Discover" },
  };
}
